import * as React from 'react'

import type { ProjectChoice } from './quick-activity'

/**
 * Funil "Nova atividade": escolher o projeto → preencher → ver o card no quadro.
 *
 * Mora no shell, acima das rotas, e não numa página: ao escolher o projeto o
 * quadro dele abre AO FUNDO e o popup continua na frente — a pessoa preenche
 * já vendo as colunas para onde a atividade vai. Montado dentro de uma rota,
 * a navegação desmontaria o próprio popup.
 *
 * Os passos (seletor de projeto e formulário) vêm sob demanda de
 * `QuickActivitySteps`: aqui fica só o atalho e o estado do funil.
 */

interface QuickActivityApi {
  readonly open: () => void
}

const QuickActivityContext = React.createContext<QuickActivityApi | null>(null)

export function useQuickActivity(): QuickActivityApi {
  const api = React.useContext(QuickActivityContext)
  if (!api) throw new Error('useQuickActivity fora de <QuickActivityProvider>')
  return api
}

const ProjectPickerDialog = React.lazy(() =>
  import('./QuickActivitySteps').then((m) => ({ default: m.ProjectPickerDialog })),
)
const FillStep = React.lazy(() => import('./QuickActivitySteps').then((m) => ({ default: m.FillStep })))

type Step = { readonly kind: 'fechado' } | { readonly kind: 'escolher' } | { readonly kind: 'preencher'; readonly choice: ProjectChoice }

export function QuickActivityProvider({ children }: { children: React.ReactNode }) {
  const [step, setStep] = React.useState<Step>({ kind: 'fechado' })
  const open = React.useCallback(() => setStep({ kind: 'escolher' }), [])

  // Alt+N — atalho da ação primária. Alt e não Ctrl: Ctrl+N abre janela nova
  // no navegador e não pode ser sequestrado. `code` pela tecla física, e só
  // sem AltGr (no ABNT2 AltGr é Ctrl+Alt e digita caractere).
  React.useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.code !== 'KeyN' || !e.altKey || e.ctrlKey || e.metaKey || e.shiftKey) return
      e.preventDefault()
      open()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open])

  const api = React.useMemo(() => ({ open }), [open])

  return (
    <QuickActivityContext.Provider value={api}>
      {children}
      {/* Sem fallback visível: o passo aparece assim que o chunk chega. */}
      <React.Suspense fallback={null}>
        {step.kind === 'escolher' && (
          <ProjectPickerDialog
            onClose={() => setStep({ kind: 'fechado' })}
            onPick={(choice) => setStep({ kind: 'preencher', choice })}
          />
        )}
        {step.kind === 'preencher' && (
          <FillStep
            choice={step.choice}
            onBack={() => setStep({ kind: 'escolher' })}
            onDone={() => setStep({ kind: 'fechado' })}
          />
        )}
      </React.Suspense>
    </QuickActivityContext.Provider>
  )
}
